import { ArrowLeft, Check, ClipboardList, FileText, Loader2, Lock, Search, Sparkles } from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { cn } from '@/lib/utils';

type ScreenStatus = 'locked' | 'pending' | 'complete' | 'blocked';

interface ScreeningV2SidebarProps {
  currentScreen: 1 | 2 | 3 | 4;
  screen1Status: 'pending' | 'complete' | 'blocked';
  screen2Status: ScreenStatus;
  screen3Status: ScreenStatus;
  screen4Status: ScreenStatus;
  onNavigate: (screen: 1 | 2 | 3 | 4) => void;
  /** Whether to show the Collect step (hidden for case types without P1/P2 questions) */
  showCollectStep?: boolean;
  /** Whether auto-save is currently in progress */
  isAutoSaving?: boolean;
  /** Whether there are unsaved changes */
  hasUnsavedChanges?: boolean;
  /** Manually trigger save */
  onSaveNow?: () => void;
}

// Exit / Save button component
// Shows: spinner when saving, save icon when unsaved changes, back arrow otherwise
interface ExitButtonProps {
  isAutoSaving?: boolean;
  hasUnsavedChanges?: boolean;
  onSaveNow?: () => void;
}

const ExitButton: React.FC<ExitButtonProps> = ({ isAutoSaving, hasUnsavedChanges, onSaveNow }) => {
  const navigate = useNavigate();
  const [showTooltip, setShowTooltip] = useState(false);

  const handleClick = async () => {
    // Flush any pending changes before navigating (auto-save is fast, but be safe)
    if (hasUnsavedChanges && onSaveNow) {
      await onSaveNow();
    }
    const intakesSearch = sessionStorage.getItem('intakesSearch') || '';
    navigate(`/intakes${intakesSearch}`);
  };

  return (
    <div
      className="relative"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <button
        type="button"
        onClick={handleClick}
        disabled={isAutoSaving}
        className={cn(
          'w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200',
          isAutoSaving
            ? 'text-amber-400 cursor-wait'
            : 'text-zinc-400 hover:text-white hover:bg-white/10',
        )}
      >
        {isAutoSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowLeft className="w-4 h-4" />}
      </button>

      {showTooltip && (
        <div
          className={cn(
            'absolute left-full ml-3 top-1/2 -translate-y-1/2 z-50',
            'bg-zinc-900 text-white text-xs font-semibold px-3 py-2 rounded-xl shadow-xl',
            'whitespace-nowrap animate-in fade-in slide-in-from-left-1 duration-150',
            'before:absolute before:right-full before:top-1/2 before:-translate-y-1/2',
            'before:border-4 before:border-transparent before:border-r-zinc-900'
          )}
        >
          {isAutoSaving ? 'Saving...' : 'Exit to Intakes'}
        </div>
      )}
    </div>
  );
};

interface StepItemProps {
  label: string;
  icon: React.ReactNode;
  status: ScreenStatus;
  active: boolean;
  onClick: () => void;
  lockedReason?: string;
}

const StepItem: React.FC<StepItemProps> = ({
  label,
  icon,
  status,
  active,
  onClick,
  lockedReason,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const isLocked = status === 'locked';
  const isComplete = status === 'complete';

  return (
    // Wrapper div handles hover events for all states including disabled
    <div
      className="relative"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <button
        type="button"
        onClick={onClick}
        disabled={isLocked}
        className={cn(
          'w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200',
          active && 'bg-white text-zinc-900 shadow-sm',
          !active && isComplete && 'text-emerald-400 hover:text-emerald-300 hover:bg-white/10',
          !active && !isComplete && isLocked && 'text-zinc-600 cursor-not-allowed',
          !active && !isComplete && !isLocked && 'text-zinc-400 hover:text-zinc-200 hover:bg-white/10',
        )}
      >
        {isLocked && <Lock className="w-4 h-4" />}
        {!isLocked && isComplete && <Check className="w-4 h-4" />}
        {!isLocked && !isComplete && icon}
      </button>

      {/* Custom Tooltip - shows for all states */}
      {showTooltip && (
        <div
          className={cn(
            'absolute left-full ml-3 top-1/2 -translate-y-1/2 z-50',
            'bg-zinc-900 text-white text-xs font-semibold px-3 py-2 rounded-xl shadow-xl',
            'whitespace-nowrap animate-in fade-in slide-in-from-left-1 duration-150',
            'before:absolute before:right-full before:top-1/2 before:-translate-y-1/2',
            'before:border-4 before:border-transparent before:border-r-zinc-900'
          )}
        >
          <div className="flex items-center gap-2">
            {isLocked && <Lock className="w-3 h-3 text-zinc-400" />}
            {isComplete && <Check className="w-3 h-3 text-emerald-400" />}
            <span>{label}</span>
            {isComplete && (
              <span className="text-emerald-400 text-[10px] uppercase tracking-wider">
                Done
              </span>
            )}
          </div>
          {isLocked && lockedReason && (
            <p className="text-zinc-400 text-[10px] mt-1">{lockedReason}</p>
          )}
        </div>
      )}
    </div>
  );
};

export const ScreeningV2Sidebar: React.FC<ScreeningV2SidebarProps> = ({
  currentScreen,
  screen1Status,
  screen2Status,
  screen3Status,
  screen4Status,
  onNavigate,
  showCollectStep = true,
  isAutoSaving,
  hasUnsavedChanges,
  onSaveNow,
}) => {
  return (
    <div className="absolute left-4 top-1/2 -translate-y-1/2 z-20 flex flex-col items-center gap-3">
      {/* Exit / Save button */}
      <div className="bg-zinc-900 rounded-full p-1.5 shadow-xl shadow-black/20">
        <ExitButton
          isAutoSaving={isAutoSaving}
          hasUnsavedChanges={hasUnsavedChanges}
          onSaveNow={onSaveNow}
        />
      </div>

      {/* Step buttons */}
      <div className="bg-zinc-900 rounded-full p-1.5 flex flex-col items-center gap-1 shadow-xl shadow-black/20">
        <StepItem
          label="Qualify"
          icon={<Search className="w-4 h-4" />}
          status={screen1Status}
          active={currentScreen === 1}
          onClick={() => onNavigate(1)}
        />
        {showCollectStep && (
          <StepItem
            label="Collect"
            icon={<ClipboardList className="w-4 h-4" />}
            status={screen2Status}
            active={currentScreen === 2}
            onClick={() => screen2Status !== 'locked' && onNavigate(2)}
            lockedReason="Complete qualification first"
          />
        )}
        <StepItem
          label="Review"
          icon={<FileText className="w-4 h-4" />}
          status={screen3Status}
          active={currentScreen === 3}
          onClick={() => screen3Status !== 'locked' && onNavigate(3)}
          lockedReason="Complete data collection first"
        />
        <StepItem
          label="AI Extract"
          icon={<Sparkles className="w-4 h-4" />}
          status={screen4Status}
          active={currentScreen === 4}
          onClick={() => screen4Status !== 'locked' && onNavigate(4)}
          lockedReason="Coming soon..."
        />
      </div>
    </div>
  );
};

export default ScreeningV2Sidebar;
